import { z } from 'zod';
import {
  AllocationStatus,
  AntiPattern,
  ClassificationClass,
  ConfidenceBand,
  ConfidenceMethod,
  Criticality,
  DecisionStatus,
  Disposition,
  IssueSeverity,
  Lifecycle,
  MappingSetStatus,
  ProcessLevel,
  ProductHealth,
  RelationshipRole,
  RelationshipStatus,
  TechnicalHealth,
} from './vocabularies';
import { IsoDate, Timestamp, Uuid } from './envelope';
import { AllocatedTotal, ChartPayload, MetricUnit, MetricValue, Money } from './metric';
import { Paged } from './query';

/**
 * Read-model projections — the contracts the screens bind to.
 *
 * Source: Canonical Entity Model §8 (eight projections), plus "Candidate
 * Assessment Result" from Prompt 1, plus the wizard's own working projection.
 * Shaped against the reference images so that a screen can be built without
 * inventing a data shape and then discovering it disagrees with the API.
 *
 * The binding rule (Prompt 3.16) applies to everything in this file: a page may
 * depend only on these types. It must not be able to tell whether the rows came
 * from a spreadsheet, an in-memory fixture, PostgreSQL or a future connector.
 */

// ── Shared fragments ─────────────────────────────────────────────────────────

/** Minimal reference to another entity, enough to render a link and a badge. */
export const EntityRef = z.object({
  id: Uuid,
  entityType: z.string(),
  businessCode: z.string().nullable(),
  name: z.string(),
  route: z.string().nullable(),
});
export type EntityRef = z.infer<typeof EntityRef>;

export const OwnerRef = z.object({
  id: Uuid,
  displayName: z.string(),
  jobTitle: z.string().nullable(),
  /** Masked to null when workforce data is restricted for this caller. */
  email: z.string().nullable(),
});
export type OwnerRef = z.infer<typeof OwnerRef>;

/** A trend sparkline. Points are period-ordered. */
export const Trend = z.object({
  points: z.array(z.number().nullable()),
  direction: z.enum(['up', 'down', 'flat']),
  changePercent: z.number().nullable(),
});
export type Trend = z.infer<typeof Trend>;

export const ProvenanceBadge = z.object({
  sourceSystem: z.string().nullable(),
  refreshedAt: Timestamp.nullable(),
  confidence: ConfidenceBand.nullable(),
  qualityScore: z.number().min(0).max(100).nullable(),
});
export type ProvenanceBadge = z.infer<typeof ProvenanceBadge>;

// ─────────────────────────────────────────────────────────────────────────────
// 1. PortfolioSummary — /portfolio/hierarchy   (ref: 01_Portfolio_Hierarchy.png)
// ─────────────────────────────────────────────────────────────────────────────

export const HierarchyNodeType = z.enum([
  'Enterprise',
  'OrganizationalUnit',
  'ValueStream',
  'ProductGroup',
  'Product',
]);
export type HierarchyNodeType = z.infer<typeof HierarchyNodeType>;

/**
 * A node in the portfolio tree.
 *
 * The type is declared by hand rather than inferred because the schema is
 * recursive — `children` refers back to the node — and TypeScript cannot infer a
 * type that appears in its own initializer. Declaring the shape and then
 * annotating the schema with `z.ZodType<HierarchyNode>` gives us both a working
 * runtime validator and a usable type.
 *
 * Counts are de-duplicated across many-to-many edges: an application supporting
 * three products is counted once at any ancestor. "No metric double counts
 * allocations" is a QA gate, not an aspiration.
 */
export type HierarchyNode = {
  id: string;
  entityType: HierarchyNodeType;
  businessCode: string | null;
  name: string;
  /** Display index such as "1.1.2", rendered before the name in the tree. */
  ordinal: string | null;
  parentId: string | null;
  depth: number;
  productCount: number;
  applicationCount: number;
  health: ProductHealth;
  healthScore: number | null;
  owner: OwnerRef | null;
  hasChildren: boolean;
  /** Null until the node is expanded — the tree lazy-loads its children. */
  children: HierarchyNode[] | null;
};

export const HierarchyNode: z.ZodType<HierarchyNode> = z.lazy(() =>
  z.object({
    id: Uuid,
    entityType: HierarchyNodeType,
    businessCode: z.string().nullable(),
    name: z.string(),
    ordinal: z.string().nullable(),
    parentId: Uuid.nullable(),
    depth: z.number().int().nonnegative(),
    productCount: z.number().int().nonnegative(),
    applicationCount: z.number().int().nonnegative(),
    health: ProductHealth,
    healthScore: z.number().min(0).max(100).nullable(),
    owner: OwnerRef.nullable(),
    hasChildren: z.boolean(),
    children: z.array(HierarchyNode).nullable(),
  }),
);

/** One ring segment of the composition sunburst. */
export const CompositionNode = z.object({
  /**
   * Identifies the arc. A record id where the arc *is* a record, and a stable
   * synthetic key where it is not.
   *
   * This was `Uuid`, which held only while the chart grouped by department and
   * value stream — both entities. Two of the four groupings it offers are not:
   * a classification band and "not in a product group" are values and
   * absences, and neither has a row to point at. Requiring a uuid meant the
   * response failed its own contract the moment those groupings were built,
   * and the screen sat in a loading skeleton rather than saying so.
   *
   * Synthetic keys are namespaced — `classification:BusinessProduct`,
   * `product-group:none` — so a consumer can tell the two apart rather than
   * discovering it by passing one to something expecting an entity.
   */
  id: z.string().min(1),
  label: z.string(),
  parentId: z.string().min(1).nullable(),
  value: z.number(),
  percentOfParent: z.number().min(0).max(1),
  /** Token name, not a hex value — the design system resolves the colour. */
  colorToken: z.string(),
});
export type CompositionNode = z.infer<typeof CompositionNode>;

export const StatusBucket = z.object({
  status: ProductHealth,
  count: z.number().int().nonnegative(),
  percent: z.number().min(0).max(1),
});
export type StatusBucket = z.infer<typeof StatusBucket>;

/**
 * The row behind the product list screen. Promoted here from
 * `packages/testing/src/types.ts`, which carried it (and `PagedRows`) marked
 * "shaped to be promoted into contracts unchanged" from the day it was
 * written — `apps/api/src/routes/products.ts` is the first real consumer that
 * made the promotion the right call rather than a hypothetical one: a
 * production route has no business importing types from a package whose job
 * is to be swapped out.
 */
export const ProductListRow = z.object({
  id: Uuid,
  businessCode: z.string().min(1),
  name: z.string().min(1),
  classification: ClassificationClass,
  classificationLabel: z.string().min(1),
  valueStream: EntityRef.nullable(),
  productGroup: EntityRef.nullable(),
  department: EntityRef.nullable(),
  owner: OwnerRef.nullable(),
  lifecycle: Lifecycle,
  health: ProductHealth,
  healthScore: z.number().min(0).max(100).nullable(),
  trend: Trend,
  strategicTierLabel: z.string().nullable(),
  applicationCount: z.number().int().nonnegative(),
  processCount: z.number().int().nonnegative(),
  /** Null whenever cost data is unavailable or restricted — never zero. */
  investmentYtd: Money.nullable(),
  benefitRealizedYtd: Money.nullable(),
  businessValue: Money.nullable(),
  provenance: ProvenanceBadge,
  route: z.string().min(1),
});
export type ProductListRow = z.infer<typeof ProductListRow>;

/**
 * A page of rows plus enough to know whether there are more. `hasMore` rather
 * than a raw offset/cursor because every current caller loads a full scoped
 * result set client-side (see apps/web's own windowing in
 * ProductListGlassPage) — this is here so a route can say "there is more"
 * honestly once one actually needs to paginate server-side, not because
 * anything does yet.
 */
export interface PagedRows<T> {
  rows: T[];
  totalRows: number | null;
  hasMore: boolean;
}

export function pagedRows<T extends z.ZodTypeAny>(item: T) {
  return z.object({
    rows: z.array(item),
    totalRows: z.number().int().nonnegative().nullable(),
    hasMore: z.boolean(),
  });
}

// ── Delivery overview (ref: pv3/Picture2.png) ──────────────────────────────
// Promoted from packages/testing/src/types.ts (apps/api/src/routes/delivery.ts
// is the reason — same as ProductListRow/PagedRows above). Several fields were
// widened to `| null` in the move: this schema now also has to describe what
// Postgres genuinely tracks, not only what the fixture always had to hand.
// core.team carries no product or value-stream link and fact.team_capacity has
// no internal/contractor split (see load-seed-data.ts's teams and delivery
// telemetry sections), and there is no scoring table backing a composite flow
// health figure or any AI-usage tracking anywhere in the schema — so
// `valueStream`, the team FTE fields, `flowHealth` and `aiImpact` are all
// nullable now, honestly, rather than fixture constants with nowhere real to
// come from.

export const DeliveryFlowStageRow = z.object({
  stage: z.enum(['Ideas', 'Discovery', 'Build', 'Release', 'Outcome']),
  /**
   * Null means the stage is not tracked by the source, which is a different
   * claim from zero items being in it. Rendering an untracked stage as `0`
   * next to real counts asserts a measurement that was never taken.
   */
  count: z.number().int().nonnegative().nullable(),
  averageWaitDays: z.number().nullable(),
  averageCycleDays: z.number().nullable(),
});
export type DeliveryFlowStageRow = z.infer<typeof DeliveryFlowStageRow>;

export const DeliveryTeamRow = z.object({
  team: EntityRef,
  valueStream: EntityRef.nullable(),
  internalFte: z.number().nullable(),
  /** Null when the caller is not entitled to workforce data, or when it is not tracked at all. */
  contractorFte: z.number().nullable(),
  capacityPerIteration: z.number().nullable(),
  productCount: z.number().int().nonnegative(),
  vacancyCount: z.number().int().nonnegative().nullable(),
});
export type DeliveryTeamRow = z.infer<typeof DeliveryTeamRow>;

export const DeliveryReleaseRow = z.object({
  code: z.string(),
  name: z.string(),
  product: EntityRef,
  plannedDate: IsoDate,
  /*
    Five stages, matching what the estate records.

    0058 began recording `InTest` and `Deploying` so the release-flow card could
    stop hard-coding a zero and stop showing one figure under two headings. This
    enum was not widened with it, so the delivery endpoint started returning
    rows its own contract rejected — and the screen sat in skeletons for anyone
    whose release train happened to contain one. A contract is only a guarantee
    if it is changed alongside the data it describes.
  */
  status: z.enum(['Planned', 'InFlight', 'InTest', 'Deploying', 'Released', 'Cancelled']),
});
export type DeliveryReleaseRow = z.infer<typeof DeliveryReleaseRow>;

export const DeliveryOverview = z.object({
  kpis: z.array(MetricValue),
  flowStages: z.array(DeliveryFlowStageRow),
  cumulativeFlow: ChartPayload,
  cycleTimeTrend: ChartPayload,
  /** Null: no scoring table backs a composite flow-health figure in this schema. */
  flowHealth: z
    .object({
      score: z.number(),
      band: ProductHealth,
      dimensions: z.array(
        z.object({ dimension: z.string(), status: ProductHealth, label: z.string() }),
      ),
    })
    .nullable(),
  releaseTrain: z.array(DeliveryReleaseRow),
  teams: z.array(DeliveryTeamRow),
  bottlenecks: z.array(
    z.object({
      id: z.string(),
      from: EntityRef,
      to: EntityRef,
      kind: z.enum(['Dependency', 'Bottleneck']),
      severity: z.enum(['Error', 'Warning', 'Info']),
      detail: z.string(),
    }),
  ),
  incidentSummary: z.object({
    p1: z.number().int().nonnegative(),
    p2: z.number().int().nonnegative(),
    p3: z.number().int().nonnegative(),
    meanTimeToRestoreHours: z.number().nullable(),
    changeFailureRate: z.number().nullable(),
  }),
  /** Null: no AI-usage tracking table or column exists anywhere in the schema. */
  aiImpact: z
    .object({
      aiAssistedWorkPercent: z.number(),
      // Both require an item-level "this specific unit of work carried an AI
      // assistance event" record, which nothing in this schema captures —
      // fact.ai_impact is a product-level aggregate (use cases, automation
      // rate, savings), not an event log. Null rather than the fixture's
      // invented multipliers (workItems.length * 0.28, useCases * 3).
      itemsAssisted: z.number().int().nonnegative().nullable(),
      defectsPrevented: z.number().int().nonnegative().nullable(),
      estimatedValue: Money.nullable(),
    })
    .nullable(),
});
export type DeliveryOverview = z.infer<typeof DeliveryOverview>;

// ── Investment overview (ref: pv3/Picture1.png) ────────────────────────────
// Promoted from packages/testing/src/types.ts (apps/api/src/routes/
// investment.ts is the reason — same as DeliveryOverview above).
// `capexVersusOpex` and `benefitRealization.forecast`/`forecastPercentOfTarget`
// are dropped from what the fixture always filled: the fixture derived them
// from an invented fixed split (24/76) and an invented multiplier (1.92x)
// respectively, neither backed by anything in the seed. A number with no real
// basis is worse than an honestly missing one, so the real backend never
// produces them — see investment.ts for the full accounting of what is real
// and what is not.

export const InvestmentProductRow = z.object({
  product: EntityRef,
  investmentYtd: Money.nullable(),
  spendYtd: Money.nullable(),
  runPercent: z.number().min(0).max(1).nullable(),
  changePercent: z.number().min(0).max(1).nullable(),
  throughputFeatures: z.number().int().nonnegative(),
  throughputReleases: z.number().int().nonnegative(),
  benefitsRealized: Money.nullable(),
  riskLevel: z.enum(['Low', 'Medium', 'High']),
  roiStatus: z.enum(['OnTrack', 'AtRisk', 'Behind']),
  varianceVsTarget: Money.nullable(),
  trend: Trend,
});
export type InvestmentProductRow = z.infer<typeof InvestmentProductRow>;

export const InvestmentOverview = z.object({
  kpis: z.array(MetricValue),
  allocationByProduct: z
    .object({
      total: AllocatedTotal,
      slices: z.array(
        z.object({
          label: z.string(),
          amount: Money,
          percent: z.number(),
          colorToken: z.string(),
          /**
           * The same amount split by capitalisation.
           *
           * Null on the residual slice: cost with no approved allocation to a
           * product still has a capitalisation classification, but attributing
           * it to a product is exactly what nobody has done, so splitting it
           * here would answer a question the row exists to say is unanswered.
           */
          capex: Money.nullable(),
          opex: Money.nullable(),
        }),
      ),
    })
    .nullable(),
  runVersusChange: z
    .object({
      runPercent: z.number(),
      changePercent: z.number(),
      runAmount: Money,
      changeAmount: Money,
    })
    .nullable(),
  /** Null: no capex/opex flag exists anywhere in the schema — see the note above. */
  capexVersusOpex: z.object({ capex: Money, opex: Money, capexPercent: z.number() }).nullable(),
  workforceMix: z
    .object({
      internalFte: z.number().nullable(),
      contractorFte: z.number().nullable(),
      internalPercent: z.number().nullable(),
      /**
       * Non-human workers — RPA, scheduled automation, AI agents — counted
       * separately because an agent is not an FTE. Null where no team has been
       * surveyed for them, never 0: "we run no agents" and "nobody has asked"
       * are different answers.
       */
      agentCount: z.number().nullable(),
      /**
       * The agents themselves, so the count can be checked rather than
       * trusted. Empty where none are recorded — which is a different
       * statement from `agentCount` being null.
       */
      agents: z
        .array(
          z.object({
            code: z.string(),
            name: z.string(),
            type: z.string(),
            owner: z.string().nullable(),
          }),
        )
        .default([]),
      /** Summed from the site recorded on each team, so it always reconciles
       *  to the headcount above rather than being its own set of percentages. */
      locations: z.array(
        z.object({
          label: z.string(),
          percent: z.number(),
          internalFte: z.number(),
          contractorFte: z.number(),
        }),
      ),
    })
    .nullable(),
  supplierConcentration: z
    .object({
      topFivePercent: z.number(),
      suppliers: z.array(
        z.object({ supplier: EntityRef, percent: z.number(), annualSpend: Money.nullable() }),
      ),
    })
    .nullable(),
  costPerProduct: ChartPayload,
  benefitRealization: z
    .object({
      realized: Money,
      target: Money,
      percentOfTarget: z.number().nullable(),
      /** Null: no forecasting model exists anywhere in the schema — see the note above. */
      forecast: Money.nullable(),
      forecastPercentOfTarget: z.number().nullable(),
    })
    .nullable(),
  bridgeToTarget: ChartPayload,
  products: z.array(InvestmentProductRow),
});
export type InvestmentOverview = z.infer<typeof InvestmentOverview>;

// ── Approval queue ─────────────────────────────────────────────────────────
// Decisions awaiting an approval, with the reason this caller can or cannot
// give it. `canApprove` is per row rather than per screen: whether you may
// approve depends on who submitted *that* decision, not only on your role.

export const ApprovalQueueRow = z.object({
  decisionId: Uuid,
  title: z.string(),
  decisionType: z.string(),
  rationale: z.string().nullable(),
  outcome: z.string().nullable(),
  status: DecisionStatus,
  submittedAt: z.string().nullable(),
  submittedBy: z.string().nullable(),
  subject: EntityRef.nullable(),
  canApprove: z.boolean(),
  /** Why this row cannot be approved by this caller. Null when it can. */
  blockedReason: z.string().nullable(),
});
export type ApprovalQueueRow = z.infer<typeof ApprovalQueueRow>;

export const ApprovalQueueView = z.object({
  data: pagedRows(ApprovalQueueRow),
  meta: z.object({ approverName: z.string(), canApprove: z.boolean() }),
});
export type ApprovalQueueView = z.infer<typeof ApprovalQueueView>;

export const ProductRiskRow = z.object({
  product: EntityRef,
  businessOwner: OwnerRef.nullable(),
  health: ProductHealth,
  healthScore: z.number().min(0).max(100),
  trend: Trend,
  /** Short codes rendered as icon chips, e.g. 'reliability', 'cost', 'delivery'. */
  riskDrivers: z.array(z.object({ code: z.string(), label: z.string() })),
});
export type ProductRiskRow = z.infer<typeof ProductRiskRow>;

export const OwnershipSummary = z.object({
  categories: z.array(
    z.object({
      category: z.enum(['Business', 'Product', 'Technology', 'Data']),
      subjectCount: z.number().int().nonnegative(),
      subjectLabel: z.string(),
      assignedPercent: z.number().min(0).max(1),
      unassignedCount: z.number().int().nonnegative(),
    }),
  ),
});
export type OwnershipSummary = z.infer<typeof OwnershipSummary>;

// ─────────────────────────────────────────────────────────────────────────────
// 1b. The risk register — /v2/risks, /v2/portfolio, /v2/products/:id/risks
// ─────────────────────────────────────────────────────────────────────────────

/**
 * A risk, which is a statement by a person and not a measurement of a system.
 *
 * `core.risk` held zero rows for the whole life of this schema, and the Product
 * 360 screen filled a tab labelled "risks" with technical-health dimensions
 * instead. They are different things: a health score says how something is
 * doing now, a risk says what somebody thinks might happen. This model is the
 * second one, and nothing on it is derived from a measurement.
 *
 * `severityScore` is likelihood × impact, 1–25, computed by the database as a
 * generated column so the register, the portfolio roll-up and any BI feed
 * reading the table get one number rather than three implementations of the
 * same multiplication.
 */
export const RiskStatus = z.enum(['Open', 'Mitigating', 'Accepted', 'Closed']);
export type RiskStatus = z.infer<typeof RiskStatus>;

export const RiskSeverityBand = z.enum(['Low', 'Moderate', 'High', 'Critical']);
export type RiskSeverityBand = z.infer<typeof RiskSeverityBand>;

export const RiskLikelihood = z.enum(['Rare', 'Unlikely', 'Possible', 'Likely', 'AlmostCertain']);
export type RiskLikelihood = z.infer<typeof RiskLikelihood>;

export const RiskImpact = z.enum(['Negligible', 'Minor', 'Moderate', 'Major', 'Severe']);
export type RiskImpact = z.infer<typeof RiskImpact>;

export const RiskCategory = z.enum(['Business', 'Technology', 'Delivery', 'Data', 'Commercial']);
export type RiskCategory = z.infer<typeof RiskCategory>;

export const RiskRow = z.object({
  id: Uuid,
  title: z.string().min(1),
  description: z.string().nullable(),
  /** Null for a risk against the portfolio rather than against one thing in it. */
  subject: EntityRef.nullable(),
  category: RiskCategory,
  likelihood: RiskLikelihood,
  impact: RiskImpact,
  severityScore: z.number().int().min(1).max(25),
  severityBand: RiskSeverityBand,
  status: RiskStatus,
  /**
   * Null is a position, not a gap. "We have looked at this and are carrying it"
   * is what Accepted with no mitigation means, and forcing a sentence into the
   * field to satisfy a schema would make the register less readable, not more.
   */
  mitigation: z.string().nullable(),
  owner: OwnerRef.nullable(),
  raisedAt: Timestamp,
  raisedBy: z.string().nullable(),
  closedAt: Timestamp.nullable(),
  closedBy: z.string().nullable(),
  /**
   * The objectives whoever raised this judged it to threaten.
   *
   * Recorded, never derived. A risk and an objective sharing a product does not
   * make one a threat to the other — an unpatched database and a retention
   * breach sit on the same estate and endanger different commitments — so the
   * link exists only where somebody asserted it, and an empty list means
   * nobody has, not that nothing is at stake.
   */
  threatens: z.array(
    z.object({ id: Uuid, objective: z.string(), level: z.string() }),
  ),
});
export type RiskRow = z.infer<typeof RiskRow>;

export const RiskRegisterView = z.object({
  rows: z.array(RiskRow),
  totalRows: z.number().int().nonnegative(),
  /** Open risks per band, for the portfolio roll-up. Closed ones are excluded. */
  openByBand: z.array(
    z.object({ band: RiskSeverityBand, count: z.number().int().nonnegative() }),
  ),
  openByCategory: z.array(
    z.object({ category: RiskCategory, count: z.number().int().nonnegative() }),
  ),
  /**
   * True when nobody has ever raised one, which reads differently from a
   * register that has been worked through and emptied. Zero open risks after
   * forty were closed is an achievement; zero because nobody looked is not.
   */
  isEmptyRegister: z.boolean(),
});
export type RiskRegisterView = z.infer<typeof RiskRegisterView>;


export const PortfolioSummaryView = z.object({
  root: HierarchyNode,
  breadcrumb: z.array(EntityRef),
  kpis: z.array(MetricValue),
  composition: z.array(CompositionNode),
  compositionGroupBy: z.enum(['productGroup', 'valueStream', 'department', 'classification']),
  healthDistribution: z.array(StatusBucket),
  healthOverTime: ChartPayload,
  /**
   * The health watchlist. Named `topRisks` since before there was a risk
   * register, and it is not one — every row is derived from a health score.
   * `riskRegister` below is the register. Both are on the screen because they
   * answer different questions, and the labels say which is which.
   */
  topRisks: z.array(ProductRiskRow),
  /**
   * Open risks somebody actually raised, rolled up by severity.
   *
   * Null when the register has never been used, which is a different statement
   * from "no open risks" and must not be rendered as a zero.
   */
  riskRegister: z
    .object({
      openTotal: z.number().int().nonnegative(),
      openByBand: z.array(
        z.object({ band: RiskSeverityBand, count: z.number().int().nonnegative() }),
      ),
      /** The most severe few, so the roll-up is clickable rather than decorative. */
      top: z.array(RiskRow),
    })
    .nullable(),
  ownership: OwnershipSummary,
  quickMetrics: z.array(MetricValue),
});
export type PortfolioSummaryView = z.infer<typeof PortfolioSummaryView>;

// ─────────────────────────────────────────────────────────────────────────────
// 1b-ii. The capability model — /v2/capabilities
// ─────────────────────────────────────────────────────────────────────────────

export const CapabilityMaturity = z.enum([
  'NotAssessed',
  'Initial',
  'Developing',
  'Defined',
  'Managed',
  'Optimising',
]);
export type CapabilityMaturity = z.infer<typeof CapabilityMaturity>;

/**
 * What the business is able to do, independent of who does it or what runs it.
 *
 * `core.application.business_capability` was a free-text column — sixty
 * distinct strings whose only consumer was a duplicate pairing that grouped
 * applications which happened to have typed the same words. A string cannot
 * carry an owner, a maturity, a parent or a cost, which is why "what does this
 * capability cost us?" could not be asked at all.
 *
 * `runCost` is nullable twice over and the two cases are different: null with
 * `costIsRestricted` false means no application delivering it records a cost,
 * and null with it true means the reader is not entitled to see one. Rendering
 * either as zero would say the capability is free.
 */
export const CapabilityRow = z.object({
  id: Uuid,
  businessCode: z.string(),
  name: z.string(),
  level: z.union([z.literal(1), z.literal(2)]),
  parent: EntityRef.nullable(),
  owner: OwnerRef.nullable(),
  maturity: CapabilityMaturity,
  /** Applications delivering it. Zero is a real finding: a capability nothing supports. */
  applicationCount: z.number().int().nonnegative(),
  productCount: z.number().int().nonnegative(),
  runCost: Money.nullable(),
  /** True when the null above is an entitlement, not an absence. */
  costIsRestricted: z.boolean(),
  /** Applications with no recorded cost, so the roll-up states its own coverage. */
  applicationsWithoutCost: z.number().int().nonnegative(),
});
export type CapabilityRow = z.infer<typeof CapabilityRow>;

export const CapabilityView = z.object({
  rows: z.array(CapabilityRow),
  totals: z.object({
    capabilities: z.number().int().nonnegative(),
    applications: z.number().int().nonnegative(),
    unowned: z.number().int().nonnegative(),
    notAssessed: z.number().int().nonnegative(),
    /** Capabilities nothing delivers — the finding this screen exists for. */
    undelivered: z.number().int().nonnegative(),
  }),
  attributedCost: Money.nullable(),
  /**
   * Run cost this roll-up could not attribute to any capability.
   *
   * Stated rather than dropped. A cost-per-capability table that silently omits
   * spend nobody could attribute reports a smaller estate than exists, and the
   * reader has no way to know by how much.
   */
  unattributedCost: Money.nullable(),
});
export type CapabilityView = z.infer<typeof CapabilityView>;

// ─────────────────────────────────────────────────────────────────────────────
// 1b-iii. The technology estate — /v2/technology
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Aurora's own position on a technology, not a vendor's.
 *
 * `Approved` and the rest are decisions this airline's architecture board took.
 * `NotAssessed` is the honest default and is never treated as approval —
 * counting an unassessed technology as compliant is the single easiest way to
 * report a healthy estate that nobody has looked at.
 */
export const TechnologyStandardStatus = z.enum([
  'Approved',
  'Contained',
  'Retiring',
  'Prohibited',
  'NotAssessed',
]);
export type TechnologyStandardStatus = z.infer<typeof TechnologyStandardStatus>;

export const TechnologyComponentRow = z.object({
  id: Uuid,
  businessCode: z.string(),
  name: z.string(),
  vendor: z.string().nullable(),
  category: z.enum(['Runtime', 'Database', 'Platform', 'Integration']).nullable(),
  standardStatus: TechnologyStandardStatus,
  /**
   * The date Aurora's own commitment ends — a contract expiry or a roadmap
   * date it holds — never a vendor's published lifecycle, which this platform
   * has no basis to assert. Null where no such date is recorded, and null is
   * not safety.
   */
  supportEndDate: IsoDate.nullable(),
  /** Why that date. A date nobody can check is a date nobody should act on. */
  supportEndBasis: z.string().nullable(),
  refreshCycleMonths: z.number().int().positive().nullable(),
  applicationCount: z.number().int().nonnegative(),
});
export type TechnologyComponentRow = z.infer<typeof TechnologyComponentRow>;

/**
 * An application and what it runs on, or the fact that nobody recorded it.
 *
 * `components` is empty for 290 of Aurora's applications, whose technology is
 * 'Vendor-managed' or absent. That is a finding, not a pass: `standing` is
 * `NotRecorded` for them and they are counted separately everywhere. An
 * application whose technology nobody wrote down cannot be compliant, because
 * compliance is a claim about something.
 */
export const ApplicationTechnologyRow = z.object({
  application: EntityRef,
  criticality: Criticality,
  technicalHealthScore: z.number().min(0).max(100).nullable(),
  owner: OwnerRef.nullable(),
  components: z.array(
    z.object({
      id: Uuid,
      name: z.string(),
      category: z.string().nullable(),
      standardStatus: TechnologyStandardStatus,
      supportEndDate: IsoDate.nullable(),
    }),
  ),
  /**
   * `PastSupport` — at least one component is past its recorded end date.
   * `ApproachingSupport` — within a year of one.
   * `Retiring` — on a technology the board is exiting, with no date recorded.
   * `Standard` — everything recorded is Approved.
   * `Contained` — everything recorded is Approved or Contained.
   * `NotRecorded` — nothing is recorded, so no claim can be made.
   */
  standing: z.enum([
    'PastSupport',
    'ApproachingSupport',
    'Retiring',
    'Contained',
    'Standard',
    'NotRecorded',
  ]),
  /** The date that decided it, where a date did. */
  earliestSupportEnd: IsoDate.nullable(),
  reason: z.string(),
});
export type ApplicationTechnologyRow = z.infer<typeof ApplicationTechnologyRow>;

export const TechnologyEstateView = z.object({
  components: z.array(TechnologyComponentRow),
  /** Applications whose standing needs an answer, worst first. */
  obsolescence: z.array(ApplicationTechnologyRow),
  totals: z.object({
    applications: z.number().int().nonnegative(),
    pastSupport: z.number().int().nonnegative(),
    approachingSupport: z.number().int().nonnegative(),
    retiring: z.number().int().nonnegative(),
    /**
     * Applications with no technology recorded at all.
     *
     * Reported beside the compliant count and never folded into it. An estate
     * screen that quietly assessed 1,610 of 1,900 applications and printed no
     * note would be claiming far more than its data supports.
     */
    notRecorded: z.number().int().nonnegative(),
    standard: z.number().int().nonnegative(),
  }),
  asOf: IsoDate,
});
export type TechnologyEstateView = z.infer<typeof TechnologyEstateView>;

// ─────────────────────────────────────────────────────────────────────────────
// 1b-iv. Impact analysis — what breaks if this goes
// ─────────────────────────────────────────────────────────────────────────────

/**
 * One thing affected by the removal of another, and how far away it is.
 *
 * `depth` is on every row and is never collapsed. "Directly affected" and
 * "affected at depth 3" are different claims: the first means this system talks
 * to the one being removed, the second means a chain of three does. Presenting
 * them as one number misstates how far a failure actually reaches, and the
 * product already applies that reasoning to the forward traversal on the
 * Product 360 screen.
 *
 * `via` names the edge that carried the reach, so a reader can argue with the
 * path rather than only with the conclusion.
 */
export const ImpactedEntity = z.object({
  entity: EntityRef,
  entityType: z.string(),
  depth: z.number().int().positive(),
  /** The relationship type this hop crossed. */
  via: z.string(),
  /** The thing one hop closer, so the chain can be followed back. */
  through: EntityRef.nullable(),
  criticality: Criticality.nullable(),
  owner: OwnerRef.nullable(),
});
export type ImpactedEntity = z.infer<typeof ImpactedEntity>;

export const ImpactAnalysisView = z.object({
  subject: EntityRef,
  subjectType: z.string(),
  /** How far the walk went. Stated because the answer depends on it. */
  maxDepth: z.number().int().positive(),
  impacted: z.array(ImpactedEntity),
  byDepth: z.array(
    z.object({ depth: z.number().int().positive(), count: z.number().int().nonnegative() }),
  ),
  byType: z.array(
    z.object({ entityType: z.string(), count: z.number().int().nonnegative() }),
  ),
  /**
   * True when the walk stopped at `maxDepth` with more still to follow.
   *
   * A truncated answer that does not say so reads as a complete one, and on
   * this screen that is the difference between "eleven things break" and
   * "at least eleven things break".
   */
  truncated: z.boolean(),
  asOf: IsoDate,
});
export type ImpactAnalysisView = z.infer<typeof ImpactAnalysisView>;

// ─────────────────────────────────────────────────────────────────────────────
// 1c. Change events — annotations over a trend
// ─────────────────────────────────────────────────────────────────────────────

export const ChangeEventCategory = z.enum([
  'Release',
  'Reorganisation',
  'Acquisition',
  'Divestment',
  'Policy',
  'Incident',
  'Migration',
  'Other',
]);
export type ChangeEventCategory = z.infer<typeof ChangeEventCategory>;

/**
 * Something that happened, so a step in a trend line can be read.
 *
 * `occurredOn` is a date rather than a timestamp because these are business
 * events. Claiming to know the minute a reorganisation took effect would be a
 * precision nobody has.
 */
export const ChangeEventRow = z.object({
  id: Uuid,
  occurredOn: IsoDate,
  category: ChangeEventCategory,
  title: z.string().min(1),
  description: z.string().nullable(),
  /** Null means enterprise-wide, which most chart-moving events are. */
  scope: EntityRef.nullable(),
  author: z.string().nullable(),
});
export type ChangeEventRow = z.infer<typeof ChangeEventRow>;

// ─────────────────────────────────────────────────────────────────────────────
// 1d. Period comparison — /v2/portfolio/compare
// ─────────────────────────────────────────────────────────────────────────────

/**
 * One measure across two periods.
 *
 * `change` is null whenever either period is missing the measure, and
 * `absentIn` says which. That is the whole point of this model: a measure
 * recorded in June and not in March did not improve by its June value, and a
 * comparison screen that subtracts null from a number and prints the result
 * would be inventing the most persuasive kind of wrong figure there is.
 */
export const PeriodComparisonRow = z.object({
  metricId: z.string(),
  label: z.string(),
  unit: MetricUnit,
  desiredDirection: z.enum(['Up', 'Down', 'Context']),
  baselineValue: z.number().nullable(),
  comparisonValue: z.number().nullable(),
  change: z.number().nullable(),
  changePercent: z.number().nullable(),
  /** 'baseline', 'comparison', 'both', or null when it was recorded in both. */
  absentIn: z.enum(['baseline', 'comparison', 'both']).nullable(),
  /** Reader-facing sentence when a comparison cannot honestly be made. */
  absenceReason: z.string().nullable(),
  observationCount: z.object({
    baseline: z.number().int().nonnegative(),
    comparison: z.number().int().nonnegative(),
  }),
});
export type PeriodComparisonRow = z.infer<typeof PeriodComparisonRow>;

export const PeriodComparisonView = z.object({
  baseline: z.object({ start: IsoDate, end: IsoDate, label: z.string() }),
  comparison: z.object({ start: IsoDate, end: IsoDate, label: z.string() }),
  rows: z.array(PeriodComparisonRow),
  /** Events inside either window, so a movement can be attributed. */
  events: z.array(ChangeEventRow),
  /** Periods the tenant actually holds observations for, for the pickers. */
  availablePeriods: z.array(z.object({ start: IsoDate, end: IsoDate, label: z.string() })),
  comparableCount: z.number().int().nonnegative(),
  notComparableCount: z.number().int().nonnegative(),
  /**
   * How much of the metric catalogue has ever been observed.
   *
   * This screen can only compare what somebody recorded twice, and in this
   * estate almost nothing is recorded at all — so a reader who sees one row
   * concludes the comparison is broken, when the truth is that 98 of the 99
   * defined metrics have never been written to the metric store. That is a
   * different problem with a different fix, and the screen should say which
   * one it is looking at.
   */
  catalogue: z.object({
    defined: z.number().int().nonnegative(),
    observed: z.number().int().nonnegative(),
    /** The earliest observation of any metric. Null where there are none. */
    recordedFrom: IsoDate.nullable(),
  }),
});
export type PeriodComparisonView = z.infer<typeof PeriodComparisonView>;

// ─────────────────────────────────────────────────────────────────────────────
// 1e. Shares and saved views
// ─────────────────────────────────────────────────────────────────────────────

export const ShareLinkRow = z.object({
  id: Uuid,
  label: z.string(),
  viewRoute: z.string(),
  viewQuery: z.string(),
  scopeLabel: z.string(),
  canSeeFinance: z.boolean(),
  canSeeWorkforce: z.boolean(),
  createdBy: z.string().nullable(),
  createdAt: Timestamp,
  expiresAt: Timestamp,
  revokedAt: Timestamp.nullable(),
  viewCount: z.number().int().nonnegative(),
  lastViewedAt: Timestamp.nullable(),
  /** Derived server-side so the list and the resolver agree on what "live" means. */
  status: z.enum(['Active', 'Expired', 'Revoked']),
});
export type ShareLinkRow = z.infer<typeof ShareLinkRow>;

export const SavedViewRow = z.object({
  id: Uuid,
  name: z.string().min(1),
  viewRoute: z.string(),
  viewQuery: z.string(),
  grouping: z.string().nullable(),
  createdAt: Timestamp,
  updatedAt: Timestamp,
  lastOpenedAt: Timestamp.nullable(),
});
export type SavedViewRow = z.infer<typeof SavedViewRow>;

// ─────────────────────────────────────────────────────────────────────────────
// 1f. The board pack, and the shared view that renders the same content
// ─────────────────────────────────────────────────────────────────────────────

/**
 * One figure in the pack, carrying everything needed to argue with it.
 *
 * `value` is a formatted string rather than a number because the pack is a
 * document: the reader is not going to recompute it, and a figure formatted at
 * generation time cannot be re-rounded differently by whatever renders it.
 * `status` is what stops that being a licence to print anything — Restricted
 * and NotTracked are distinct, and neither is ever rendered as a zero.
 */
export const BoardPackFigure = z.object({
  label: z.string(),
  value: z.string(),
  status: z.enum(['Available', 'Restricted', 'NotTracked']),
  /** Why it is absent, in the reader's language. Null when it is present. */
  reason: z.string().nullable(),
  source: z.string(),
  period: z.string(),
});
export type BoardPackFigure = z.infer<typeof BoardPackFigure>;

export const BoardPackSection = z.object({
  title: z.string(),
  narrative: z.string().nullable(),
  figures: z.array(BoardPackFigure),
});
export type BoardPackSection = z.infer<typeof BoardPackSection>;

export const BoardPackView = z.object({
  title: z.string(),
  scopeLabel: z.string(),
  periodLabel: z.string(),
  generatedAt: Timestamp,
  generatedFor: z.string(),
  /** True when the reader is seeing a narrowed estate, stated on the cover. */
  isNarrowed: z.boolean(),
  sections: z.array(BoardPackSection),
  topRisks: z.array(RiskRow),
  events: z.array(ChangeEventRow),
  /** Named on the document so a reader knows what they were not shown. */
  restrictedFields: z.array(z.string()),
});
export type BoardPackView = z.infer<typeof BoardPackView>;

// ─────────────────────────────────────────────────────────────────────────────
// 2. AssessmentWizardView — /discover/wizard/:candidateId  (ref: 02_…Wizard.png)
// ─────────────────────────────────────────────────────────────────────────────

export const WizardStep = z.enum([
  'Overview',
  'Attributes',
  'Evidence',
  'Classification',
  'Review',
]);
export type WizardStep = z.infer<typeof WizardStep>;

/**
 * One scored criterion. `score` is nullable because an unanswered question is
 * distinct from a question answered as 1 — the wizard must be able to show
 * "not yet assessed" rather than defaulting to the lowest rating.
 */
export const CriterionRow = z.object({
  criterionId: z.string().regex(/^Q\d{2}$/),
  ordinal: z.number().int().positive(),
  name: z.string(),
  guidingQuestion: z.string(),
  weightPercent: z.number().min(0).max(100),
  iconToken: z.string(),
  score: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4), z.literal(5)]).nullable(),
  rationale: z.string().nullable(),
  evidenceIds: z.array(Uuid),
  isMandatory: z.boolean(),
  /** Anchor text for scores 1, 3 and 5, shown as help beneath the scale. */
  scaleAnchors: z.object({ low: z.string(), mid: z.string(), high: z.string() }),
});
export type CriterionRow = z.infer<typeof CriterionRow>;

export const ClassificationScore = z.object({
  classification: ClassificationClass,
  label: z.string(),
  /** 0–1 fit against this class. */
  fit: z.number().min(0).max(1),
  weightedScore: z.number(),
  band: z.enum(['High', 'Moderate', 'Low']),
});
export type ClassificationScore = z.infer<typeof ClassificationScore>;

export const DecisionSummary = z.object({
  recommended: ClassificationClass,
  recommendedLabel: z.string(),
  explanation: z.string(),
  confidencePercent: z.number().min(0).max(100),
  confidenceBand: ConfidenceBand,
  /** Never auto-approved. This is always false until a human decides. */
  isApproved: z.boolean(),
  modelVersion: z.string(),
  warnings: z.array(
    z.object({
      antiPattern: AntiPattern,
      message: z.string(),
      severity: IssueSeverity,
    }),
  ),
});
export type DecisionSummary = z.infer<typeof DecisionSummary>;

export const AssessmentWizardView = z.object({
  candidate: z.object({
    id: Uuid,
    businessCode: z.string(),
    name: z.string(),
    domain: z.string().nullable(),
    processContext: z.string().nullable(),
    valueStream: EntityRef.nullable(),
    l2Domain: z.string().nullable(),
    assessmentOwner: OwnerRef.nullable(),
    lastUpdated: Timestamp,
  }),
  assessmentId: Uuid,
  currentStep: WizardStep,
  completedSteps: z.array(WizardStep),
  blockedSteps: z.array(WizardStep),
  criteria: z.array(CriterionRow),
  averageScore: z.number().nullable(),
  decision: DecisionSummary.nullable(),
  scoreBreakdown: z.array(
    z.object({
      criterionId: z.string(),
      name: z.string(),
      score: z.number().nullable(),
      maxScore: z.literal(5),
    }),
  ),
  alternatives: z.array(ClassificationScore),
  /** Reflected in the save-status indicator; drafts autosave. */
  saveState: z.enum(['idle', 'saving', 'saved', 'error']),
  lastSavedAt: Timestamp.nullable(),
  isReadOnly: z.boolean(),
  canFinalize: z.boolean(),
  finalizeBlockers: z.array(z.string()),
});
export type AssessmentWizardView = z.infer<typeof AssessmentWizardView>;

// ─────────────────────────────────────────────────────────────────────────────
// 3. AssessmentResultView — /discover/results/:assessmentId  (ref: 03_…png)
// ─────────────────────────────────────────────────────────────────────────────

export const WeightedScoreRow = z.object({
  criterionId: z.string(),
  attribute: z.string(),
  weightPercent: z.number(),
  score: z.number(),
  weightedScore: z.number(),
  iconToken: z.string(),
});
export type WeightedScoreRow = z.infer<typeof WeightedScoreRow>;

export const EvidenceSummary = z.object({
  id: Uuid,
  title: z.string(),
  sourceLabel: z.string(),
  date: IsoDate.nullable(),
  status: z.enum(['Available', 'Restricted', 'Unavailable', 'Superseded']),
  url: z.string().nullable(),
  owner: OwnerRef.nullable(),
});
export type EvidenceSummary = z.infer<typeof EvidenceSummary>;

export const BoundaryItem = z.object({
  id: Uuid,
  label: z.string(),
  entityRef: EntityRef.nullable(),
  inScope: z.boolean(),
  reason: z.string().nullable(),
  evidenceId: Uuid.nullable(),
});
export type BoundaryItem = z.infer<typeof BoundaryItem>;

export const IncludedApplicationRow = z.object({
  application: EntityRef,
  businessCapability: z.string().nullable(),
  criticality: Criticality,
  roleInProduct: RelationshipRole,
});
export type IncludedApplicationRow = z.infer<typeof IncludedApplicationRow>;

export const IncludedProcessRow = z.object({
  processL2: z.string(),
  processL3: z.string(),
  processRef: EntityRef,
  roleInProduct: RelationshipRole,
});
export type IncludedProcessRow = z.infer<typeof IncludedProcessRow>;

export const ActionItemRow = z.object({
  id: Uuid,
  action: z.string(),
  owner: OwnerRef.nullable(),
  ownerOrganization: z.string().nullable(),
  targetDate: IsoDate.nullable(),
  status: z.enum(['Planned', 'InProgress', 'Complete', 'Blocked']),
});
export type ActionItemRow = z.infer<typeof ActionItemRow>;

export const ApprovalEvent = z.object({
  stage: z.string(),
  actor: OwnerRef.nullable(),
  action: z.enum(['Submitted', 'Reviewed', 'Returned', 'Approved', 'Rejected', 'Published']),
  occurredAt: Timestamp,
  comments: z.string().nullable(),
  effectiveDate: IsoDate.nullable(),
});
export type ApprovalEvent = z.infer<typeof ApprovalEvent>;

export const AssessmentResultView = z.object({
  assessment: z.object({
    id: Uuid,
    businessCode: z.string(),
    candidateName: z.string(),
    completedAt: Timestamp.nullable(),
    completedBy: OwnerRef.nullable(),
    status: DecisionStatus,
    modelVersion: z.string(),
  }),
  recommendation: z.object({
    classification: ClassificationClass,
    label: z.string(),
    fitLabel: z.string(),
    scoreOutOf100: z.number().min(0).max(100),
    rationaleSummary: z.string(),
  }),
  radar: ChartPayload,
  weightedScores: z.array(WeightedScoreRow),
  weightedTotal: z.object({ raw: z.number(), outOf100: z.number() }),
  keyReasons: z.array(z.object({ text: z.string(), sentiment: z.enum(['positive', 'caution']) })),
  evidence: z.array(EvidenceSummary),
  boundary: z.object({
    inScope: z.array(BoundaryItem),
    outOfScope: z.array(BoundaryItem),
  }),
  includedApplications: Paged_(IncludedApplicationRow),
  includedProcesses: Paged_(IncludedProcessRow),
  excludedItems: z.array(z.object({ label: z.string(), reason: z.string() })),
  ownerRecommendation: z.object({
    recommendedOwnerTitle: z.string(),
    ownerOrganization: z.string(),
    rationale: z.string().nullable(),
  }),
  gapsAndAssumptions: z.array(
    z.object({ text: z.string(), severity: IssueSeverity, kind: z.enum(['gap', 'assumption']) }),
  ),
  nextActions: z.array(ActionItemRow),
  approvals: z.array(ApprovalEvent),
  alternatives: z.array(ClassificationScore),
});
export type AssessmentResultView = z.infer<typeof AssessmentResultView>;

/**
 * Local helper so paged fragments stay readable inside larger objects. The
 * return type is written out because the helper is used above its declaration
 * and TypeScript will not infer through the forward reference.
 */
function Paged_<T extends z.ZodTypeAny>(
  row: T,
): z.ZodObject<{
  rows: z.ZodArray<T>;
  totalRows: z.ZodNullable<z.ZodNumber>;
  hasMore: z.ZodBoolean;
}> {
  return z.object({
    rows: z.array(row),
    totalRows: z.number().int().nonnegative().nullable(),
    hasMore: z.boolean(),
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// 4. Product360View — /products/:productId/:tab?   (ref: 04_Product_360.png)
// ─────────────────────────────────────────────────────────────────────────────

export const Product360Tab = z.enum([
  'overview',
  'outcomes',
  'processes',
  'applications',
  'teams',
  'delivery',
  'financials',
  'suppliers',
  'contracts',
  'risks',
  // Automation that is not machine learning is still automation, and a product
  // that reports hours saved by a rules engine belongs on this tab. The label
  // says so; the key stays `ai` because it is in URLs people have bookmarked.
  'ai',
]);
export type Product360Tab = z.infer<typeof Product360Tab>;

/**
 * One key result on Product 360, and how far it has got.
 *
 * `progressPercent` and `onTrack` are nullable, and that is the whole of this
 * type's history. Both used to be a number and a boolean, and a key result
 * nobody had measured arrived as `0` and `false` — a card reading "0% of
 * target · Off track" beside "No target set · Not measured", which is the
 * product's own central rule broken on the screen that states it most often.
 * Worse, `/v2/objectives` rolls the same key results up and excludes the
 * unmeasurable ones from its mean, so the two screens reported different
 * things about the same product.
 *
 * A null here means nobody could score it. The reason travels beside it.
 */
export const OkrCard = z.object({
  id: Uuid,
  ordinal: z.number().int().positive(),
  objective: z.string(),
  targetLabel: z.string(),
  actualLabel: z.string(),
  /** 0–1, or null where the key result cannot be scored. Never 0 for a null. */
  progressPercent: z.number().min(0).max(1).nullable(),
  /** Null where there is no progress figure to be on or off track against. */
  onTrack: z.boolean().nullable(),
  /** Why there is no figure, in the words of whoever recorded it. */
  unmeasurableReason: z.string().nullable(),
});
export type OkrCard = z.infer<typeof OkrCard>;

export const DependencyRow = z.object({
  platform: EntityRef,
  provider: z.string().nullable(),
  criticality: Criticality,
  status: ProductHealth,
  /**
   * Which layer this sits in.
   *
   * `Application` is something the product itself runs on
   * (`SUPPORTED_BY_APPLICATION`). `Platform` is something one of those
   * applications depends on in turn (`DEPENDS_ON_APPLICATION`) — the shared
   * layer underneath. The tab is titled "depended-on platforms and
   * applications" and the two are genuinely different distances from the
   * product, so collapsing them would misstate how far a failure is.
   */
  kind: z.enum(['Application', 'Platform']),
  /** For a platform: the product's applications that rely on it. */
  reliedOnBy: z.array(z.string()),
});
export type DependencyRow = z.infer<typeof DependencyRow>;

export const ReleaseFlowStage = z.object({
  stage: z.enum(['Plan', 'Build', 'Test', 'Deploy', 'Done']),
  count: z.number().int().nonnegative(),
  label: z.string(),
  /**
   * The recorded status this stage counts. Sent so the drilldown can ask for
   * the releases behind a box without the client re-deriving the mapping and
   * getting a different answer from the number it is standing on.
   */
  statusCode: z.string(),
});

/** A supplier serving this product, with what is spent with them on it. */
export const ProductSupplier = z.object({
  id: Uuid,
  name: z.string(),
  category: z.string().nullable(),
  riskStatus: z.string().nullable(),
  /** Null when the caller has no finance entitlement — Restricted, not zero. */
  spend: Money.nullable(),
  contractCount: z.number().int().nonnegative(),
});
export type ProductSupplier = z.infer<typeof ProductSupplier>;
export type ReleaseFlowStage = z.infer<typeof ReleaseFlowStage>;

export const Product360View = z.object({
  product: z.object({
    id: Uuid,
    businessCode: z.string(),
    name: z.string(),
    classification: ClassificationClass,
    domain: EntityRef.nullable(),
    productGroup: EntityRef.nullable(),
    valueStream: EntityRef.nullable(),
    owner: OwnerRef.nullable(),
    lifecycle: Lifecycle,
    health: ProductHealth,
    healthScore: z.number().min(0).max(100).nullable(),
    strategicTier: z.string().nullable(),
    strategicTierLabel: z.string().nullable(),
  }),
  headerKpis: z.array(MetricValue),
  mission: z.object({ statement: z.string(), briefUrl: z.string().nullable() }).nullable(),
  customers: z.array(
    z.object({
      id: Uuid,
      name: z.string(),
      segmentType: z.enum(['Internal', 'External']),
      isPrimary: z.boolean(),
      iconToken: z.string(),
    }),
  ),
  okrs: z.array(OkrCard),
  /**
   * The processes this product carries out, and the systems doing the work.
   *
   * Enough to draw the map in place. The Processes tab used to hold a link to
   * the mapping studio and nothing else, which asked a reader to leave the
   * product to see what the product does.
   *
   * `applications[].linkStatus` is carried per row because the two edges are
   * not equally certain: a product including a process is an approved mapping,
   * while an application implementing one is inferred from the application
   * already supporting the product. The canvas has to be able to draw that
   * difference rather than presenting both as settled.
   */
  processes: z.array(
    z.object({
      id: Uuid,
      code: z.string(),
      name: z.string(),
      level: z.enum(['L1', 'L2', 'L3', 'L4']),
      criticality: Criticality,
      /** The parent step, for the reader's bearings. Null at the top. */
      parentName: z.string().nullable(),
      applications: z.array(
        z.object({
          id: Uuid,
          businessCode: z.string().nullable(),
          name: z.string(),
          lifecycle: Lifecycle,
          technicalHealth: TechnicalHealth,
          linkStatus: z.enum(['Approved', 'InReview', 'Proposed', 'Rejected', 'Superseded']),
        }),
      ),
    }),
  ),
  boundary: z.object({ inScope: z.array(BoundaryItem), outOfScope: z.array(BoundaryItem) }),
  healthDimensions: z.array(
    z.object({ dimension: z.string(), status: ProductHealth, label: z.string() }),
  ),
  keyKpis: z.array(MetricValue),
  dependencies: z.array(DependencyRow),
  releaseFlow: z.array(ReleaseFlowStage),
  suppliers: z.array(ProductSupplier),
  upcomingReleases: z.array(z.object({ code: z.string(), name: z.string(), plannedDate: IsoDate })),
  costSummary: z
    .object({
      total: AllocatedTotal,
      percentOfPlan: z.number().nullable(),
      breakdown: z.array(z.object({ category: z.string(), amount: Money, percent: z.number() })),
    })
    .nullable(),
  benefitSummary: z
    .object({
      realized: Money,
      percentOfTarget: z.number().nullable(),
      breakdown: z.array(z.object({ category: z.string(), amount: Money, percent: z.number() })),
    })
    .nullable(),
  financialDrivers: z.array(
    z.object({
      driver: z.string(),
      type: z.string(),
      realized: Money,
      vsTargetPercent: z.number().nullable(),
    }),
  ),
  /**
   * Monetary and effort figures are individually nullable so that a caller
   * without finance entitlement receives null rather than a zero. Making the
   * whole card nullable was not enough: the use-case count is not sensitive,
   * and returning `money(0)` to express "you may not see this" is precisely the
   * failure the specification forbids.
   */
  aiImpact: z
    .object({
      useCasesInProduction: z.number().int().nonnegative(),
      automationRate: z.number().min(0).max(1),
      costSavings: Money.nullable(),
      hoursSaved: z.number().nullable(),
    })
    .nullable(),
  stats: z.object({
    goLiveDate: IsoDate.nullable(),
    totalReleases: z.number().int().nonnegative(),
    featuresDelivered: z.number().int().nonnegative(),
    activeUsers: z.number().int().nonnegative().nullable(),
    dailyTransactions: z.number().nullable(),
    integrations: z.number().int().nonnegative(),
    countries: z.number().int().nonnegative().nullable(),
  }),
  availableTabs: z.array(Product360Tab),
});
export type Product360View = z.infer<typeof Product360View>;

// ─────────────────────────────────────────────────────────────────────────────
// 5. ProcessMappingView — /mapping/process-product   (ref: 05_…Mapping.png)
// ─────────────────────────────────────────────────────────────────────────────

export const ProcessTreeNode = z.object({
  id: Uuid,
  businessCode: z.string(),
  name: z.string(),
  level: ProcessLevel,
  parentId: Uuid.nullable(),
  childCount: z.number().int().nonnegative(),
  mappedCount: z.number().int().nonnegative(),
  hasChildren: z.boolean(),
});
export type ProcessTreeNode = z.infer<typeof ProcessTreeNode>;

export const MappingNode = z.object({
  id: Uuid,
  column: z.enum(['process', 'product', 'application']),
  entityType: z.string(),
  businessCode: z.string().nullable(),
  label: z.string(),
  sublabel: z.string().nullable(),
  iconToken: z.string(),
  /** 0–100 mapping confidence for the node, shown as a small badge. */
  confidenceScore: z.number().min(0).max(100).nullable(),
  fitLabel: z.string().nullable(),
  ownerLabel: z.string().nullable(),
  isSelected: z.boolean(),
});
export type MappingNode = z.infer<typeof MappingNode>;

/**
 * An edge is a governed relationship, never a drawing. `status`, `confidence`
 * and `evidenceId` travel with it so the canvas and the list view are rendering
 * the same fact rather than two different approximations of it.
 */
export const MappingEdge = z.object({
  id: Uuid,
  fromNodeId: Uuid,
  toNodeId: Uuid,
  relationshipType: z.string(),
  role: RelationshipRole,
  status: RelationshipStatus,
  confidenceScore: z.number().min(0).max(1).nullable(),
  confidenceBand: ConfidenceBand.nullable(),
  confidenceMethod: ConfidenceMethod.nullable(),
  allocationPercent: z.number().min(0).max(1).nullable(),
  evidenceId: Uuid.nullable(),
  validFrom: IsoDate,
  validTo: IsoDate.nullable(),
  isDraft: z.boolean(),
  /** Textual label so the edge never relies on colour or dash pattern alone. */
  styleLabel: z.string(),
});
export type MappingEdge = z.infer<typeof MappingEdge>;

export const MappingQualitySummary = z.object({
  overallPercent: z.number().min(0).max(100),
  bands: z.array(
    z.object({
      /**
       * `Unrecorded` is not a fourth grade of confidence, it is the absence of
       * one. Every governed edge on the process map carries a null
       * `confidence_score` — nobody has ever scored one — and folding those
       * into `Low` would report an estate that had been assessed and found
       * doubtful, which is a different and much more alarming claim than the
       * true one.
       */
      band: z.enum(['High', 'Medium', 'Low', 'Unrecorded', 'Unmapped']),
      count: z.number().int().nonnegative(),
      percent: z.number().min(0).max(1),
    }),
  ),
  dimensions: z.array(z.object({ dimension: z.string(), percent: z.number().min(0).max(1) })),
});
export type MappingQualitySummary = z.infer<typeof MappingQualitySummary>;

export const MappingIssue = z.object({
  id: Uuid,
  kind: z.enum(['unmapped', 'overlap', 'conflict']),
  severity: IssueSeverity,
  title: z.string(),
  detail: z.string().nullable(),
  subjectRefs: z.array(EntityRef),
  hint: z.string().nullable(),
});
export type MappingIssue = z.infer<typeof MappingIssue>;

export const ProcessMappingView = z.object({
  processTree: z.array(ProcessTreeNode),
  selectedProcessId: Uuid.nullable(),
  mappingSet: z.object({
    id: Uuid,
    name: z.string(),
    version: z.number().int().positive(),
    status: MappingSetStatus,
    lastEditedAt: Timestamp.nullable(),
    isEditable: z.boolean(),
  }),
  nodes: z.array(MappingNode),
  edges: z.array(MappingEdge),
  quality: MappingQualitySummary,
  issues: z.array(MappingIssue),
  /**
   * How many findings exist of each kind, against however many `issues`
   * carries.
   *
   * The queue is capped so the rail stays a rail, and until now the cap was
   * silent: the summary counted 39 overlaps in scope while the queue showed 6
   * and said nothing, so a governance screen printed two different numbers for
   * one fact and a steward could act on neither. The list is still capped; the
   * total travels with it so the interface can say which it is showing.
   */
  issueTotals: z.object({
    unmapped: z.number().int().nonnegative(),
    overlap: z.number().int().nonnegative(),
    conflict: z.number().int().nonnegative(),
  }),
  recommendations: z.array(
    z.object({
      id: Uuid,
      iconToken: z.string(),
      text: z.string(),
      actionRoute: z.string().nullable(),
    }),
  ),
  counters: z.object({
    totalProcesses: z.number().int().nonnegative(),
    mapped: z.number().int().nonnegative(),
    unmapped: z.number().int().nonnegative(),
    overlaps: z.number().int().nonnegative(),
    conflicts: z.number().int().nonnegative(),
    /**
     * Null where no edge in the selection carries a confidence score. Zero
     * would say the mapping is certainly wrong; null says nobody has judged it.
     */
    averageConfidencePercent: z.number().min(0).max(100).nullable(),
  }),
});
export type ProcessMappingView = z.infer<typeof ProcessMappingView>;

// ─────────────────────────────────────────────────────────────────────────────
// 5b. ApplicationMappingView — /mapping/application-product
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The application-to-product studio's own read model.
 *
 * It used to have none. The screen read `ProcessMappingView` and filtered it
 * down to the edges with an application endpoint, which worked in the sense
 * that rows appeared, and was wrong in three ways that mattered: it carried a
 * `processTree` the screen never drew, it inherited counters computed over
 * processes and reported them as though they described applications, and it
 * could only ever show applications that happened to reach a process — so an
 * application mapped to a product and to nothing else was invisible on the
 * screen whose entire subject is applications mapped to products.
 *
 * `MappingNode` and `MappingEdge` are shared with the process studio on
 * purpose. The nodes and edges genuinely are the same governed records; what
 * differs is the question asked of them, which is what this view expresses.
 */

/** The working set an edge belongs to, and whether it may be changed. */
export const MappingSetRef = z.object({
  id: Uuid,
  name: z.string(),
  version: z.number().int().positive(),
  status: MappingSetStatus,
  lastEditedAt: Timestamp.nullable(),
  isEditable: z.boolean(),
});
export type MappingSetRef = z.infer<typeof MappingSetRef>;

/**
 * One row of `rel.endpoint_rule`, sent to the browser so a dialog can say what
 * may join what before somebody tries it.
 *
 * The database refuses an impermissible pair whatever the interface believes
 * (RM-003), and that refusal is the guarantee. This is only the explanation:
 * shipping the rules means the studio can name the permitted endpoints in the
 * refusal rather than saying "something went wrong", which is the difference
 * between a model that is enforced and a model that appears to be broken.
 *
 * `'*'` in either endpoint is the open-ended sentinel the evidence, decision,
 * comment and lineage types use — it means "any entity type".
 */
export const EndpointRule = z.object({
  relationshipType: z.string().min(1),
  fromEntityType: z.string().min(1),
  toEntityType: z.string().min(1),
  cardinality: z.enum(['1:1', '1:N', 'N:1', 'N:M']),
});
export type EndpointRule = z.infer<typeof EndpointRule>;

export const ApplicationMappingView = z.object({
  mappingSet: MappingSetRef,
  /** Applications and the products they support. No process nodes appear here. */
  nodes: z.array(MappingNode),
  edges: z.array(MappingEdge),
  /**
   * What the page is showing against what exists.
   *
   * The estate holds thousands of these edges and the studio paints a window
   * of them. Stating the window is the difference between a list and a claim.
   */
  page: z.object({
    returnedEdges: z.number().int().nonnegative(),
    totalEdges: z.number().int().nonnegative(),
    hasMore: z.boolean(),
  }),
  counters: z.object({
    /** Applications in the caller's span, however few of them are on screen. */
    totalApplications: z.number().int().nonnegative(),
    mappedApplications: z.number().int().nonnegative(),
    unmappedApplications: z.number().int().nonnegative(),
    productsReached: z.number().int().nonnegative(),
    proposedEdges: z.number().int().nonnegative(),
    approvedEdges: z.number().int().nonnegative(),
    supersededEdges: z.number().int().nonnegative(),
  }),
  /** Every pair `SUPPORTED_BY_APPLICATION` permits, read from the database. */
  endpointRules: z.array(EndpointRule),
});
export type ApplicationMappingView = z.infer<typeof ApplicationMappingView>;

// ─────────────────────────────────────────────────────────────────────────────
// 6. ApplicationPortfolioView — /applications   (ref: 06_Application_to_…png)
// ─────────────────────────────────────────────────────────────────────────────

export const ApplicationRow = z.object({
  id: Uuid,
  businessCode: z.string(),
  name: z.string(),
  domain: z.string().nullable(),
  businessCapability: z.string().nullable(),
  owner: OwnerRef.nullable(),
  criticality: Criticality,
  technicalHealth: TechnicalHealth,
  technicalHealthScore: z.number().min(0).max(100).nullable(),
  lifecycle: Lifecycle,
  isDuplicateCandidate: z.boolean(),
  /**
   * The supplier this application was bought from, null where it was built
   * in house. Shown on the row because the estate names applications after
   * their vendor family — "Brightpath Ledger" is a Brightpath Consulting
   * product — and a reader who does not know that reads the prefix as noise.
   */
  vendorName: z.string().nullable(),
  /** Null when the caller is not entitled to see cost. */
  annualRunCost: Money.nullable(),
  /**
   * Budget against actual, split by capitalisation.
   *
   * All four are nullable and all four mean different things when null: no
   * budget was set, nothing was spent, or the caller may not see money. The
   * table decides which to print from `meta.scope.maskedFields` rather than
   * from the null itself — a budget nobody set is not a budget of zero.
   */
  capexBudget: Money.nullable(),
  capexActual: Money.nullable(),
  opexBudget: Money.nullable(),
  opexActual: Money.nullable(),
  productCount: z.number().int().nonnegative(),
  processCount: z.number().int().nonnegative(),
  isOrphan: z.boolean(),
  provenance: ProvenanceBadge,
});
export type ApplicationRow = z.infer<typeof ApplicationRow>;

/**
 * What a cost was for, and how much of it nobody has said.
 *
 * `fact.cost_actual` gained a `cost_nature` dimension in 0075, and almost
 * nothing in this estate populates it: change spend is unambiguously Build,
 * and run spend is licence, infrastructure and people mixed together with no
 * column separating them. 1,781 of 1,972 rows are `Unclassified`.
 *
 * So this model carries `unclassified` as a first-class share rather than
 * folding it into Other. A four-way pie over a feed that classifies 9% of its
 * spend would be the most useful-looking invented figure in the product; the
 * share nobody has recorded is the number that tells a controller their feed
 * is missing a column.
 */
export const CostNature = z.enum([
  'Licence',
  'Infrastructure',
  'People',
  'Build',
  'Other',
  'Unclassified',
]);
export type CostNature = z.infer<typeof CostNature>;

export const CostComposition = z.object({
  total: Money.nullable(),
  lines: z.array(
    z.object({
      nature: CostNature,
      label: z.string(),
      amount: Money,
      /** 0–1 of the total. */
      share: z.number().min(0).max(1),
      /** True where retiring the subject actually stops this spend. */
      ceasesOnRetirement: z.boolean(),
    }),
  ),
  /** 0–1 of the total that carries no recorded nature. */
  unclassifiedShare: z.number().min(0).max(1),
});
export type CostComposition = z.infer<typeof CostComposition>;

/**
 * Where a contract stands against its own term — Expiring, Active, Evergreen or
 * Expired.
 *
 * Declared here rather than beside the rest of the contract shapes in
 * `read-models.sourcing.ts` because `ApplicationDetail` below needs it, and the
 * import rule in this package runs one way: `read-models.*` may import from
 * `read-models.ts` and never the reverse. Circular imports would be survivable
 * for types and are not survivable for Zod schemas, which are built at module
 * load.
 *
 * Derived from `end_date` and today, never stored. `Evergreen` is not a synonym
 * for "no expiry recorded": it means the agreement rolls until cancelled, which
 * the record says explicitly.
 */
export const ContractStanding = z.enum(['Expiring', 'Active', 'Evergreen', 'Expired']);
export type ContractStanding = z.infer<typeof ContractStanding>;

/**
 * Contracted commitment on one application.
 *
 * This is the shape C15 asked for when it said "feed contract value into the
 * TCO composition", and it is deliberately **beside** the recorded ledger
 * rather than inside it.
 *
 * `fact.cost_actual` already contains Aurora's licence spend; it is simply
 * unlabelled, which is what `CostComposition.unclassifiedShare` exists to say.
 * Adding contract value to those rows would count the same money twice and
 * inflate every portfolio total in the product. Reclassifying a whole ledger
 * row as Licence because a contract touches the application would assert a
 * split nobody recorded.
 *
 * So this reports the other thing, which was genuinely absent: what Aurora is
 * **contractually obliged to pay** for this application each year, the
 * agreements it comes from, and — because a licence covering forty applications
 * commits a share to each — the basis on which the share was struck. The share
 * is an even split, it says so, and `sharedAcross` gives the divisor so a
 * reader can check it rather than trust it.
 */
export const ContractedCommitment = z.object({
  /** Sum of each covering agreement's annual value times this application's share. */
  annualCommitment: Money.nullable(),
  contracts: z.array(
    z.object({
      contract: EntityRef,
      supplier: EntityRef,
      /** The agreement's whole annual value, before the share is applied. */
      annualValue: Money.nullable(),
      /** How many applications this agreement covers. The divisor. */
      sharedAcross: z.number().int().positive(),
      /** This application's share of it, 0–1. */
      share: z.number().min(0).max(1),
      /** This application's share of the value, in money. */
      attributed: Money.nullable(),
      endDate: IsoDate.nullable(),
      standing: ContractStanding,
    }),
  ),
  /**
   * Agreements covering this application that record no value, so contribute
   * nothing to the commitment. Stated rather than silently excluded.
   */
  contractsWithoutValue: z.number().int().nonnegative(),
  /** Always `Equal` in this build; the field exists so the screen never has to assume. */
  allocationBasis: z.string(),
});
export type ContractedCommitment = z.infer<typeof ContractedCommitment>;

/**
 * A service an application exposes, and the interfaces in front of it.
 *
 * `slo` and `owner` are nullable and are the two things a first service
 * register is missing most often — thirty-two of Aurora's seventy-six services
 * have no recorded service level and fourteen have no owner. The screen names
 * both absences rather than leaving a blank cell.
 */
export const ExposedApi = z.object({
  id: Uuid,
  businessCode: z.string().nullable(),
  name: z.string(),
  protocol: z.string().nullable(),
  apiVersion: z.string().nullable(),
  lifecycle: z.string(),
});
export type ExposedApi = z.infer<typeof ExposedApi>;

export const ExposedService = z.object({
  id: Uuid,
  businessCode: z.string().nullable(),
  name: z.string(),
  description: z.string().nullable(),
  serviceType: z.string().nullable(),
  slo: z.string().nullable(),
  lifecycle: z.string(),
  owner: OwnerRef.nullable(),
  apis: z.array(ExposedApi),
  /** Services that depend on this one. The reason the impact walk goes deeper. */
  dependentServiceCount: z.number().int().nonnegative(),
});
export type ExposedService = z.infer<typeof ExposedService>;

/** A data asset an application stores or processes. */
export const StoredDataAsset = z.object({
  id: Uuid,
  businessCode: z.string().nullable(),
  name: z.string(),
  assetType: z.string().nullable(),
  /** Restricted, Confidential, Internal, Public — from the entity's own classification. */
  classification: z.string(),
  schemaRef: z.string().nullable(),
  owner: OwnerRef.nullable(),
  /** True where this application is the recorded system of record for the asset. */
  isSystemOfRecord: z.boolean(),
});
export type StoredDataAsset = z.infer<typeof StoredDataAsset>;

export const ApplicationDetail = z.object({
  row: ApplicationRow,
  version: z.string().nullable(),
  vendor: z.string().nullable(),
  hostingEnvironment: z.string().nullable(),
  architecture: z.string().nullable(),
  technologyStack: z.string().nullable(),
  database: z.string().nullable(),
  dataSensitivity: z.string().nullable(),
  securityScore: z.number().min(0).max(100).nullable(),
  availability30d: z.number().min(0).max(1).nullable(),
  /** Null when no integration register is connected; 0 would claim a count nobody took. */
  integrationCount: z.number().int().nonnegative().nullable(),
  totalCostOfOwnership: Money.nullable(),
  /**
   * The same money, broken down by what it bought.
   *
   * Null when the caller may not see finance at all — distinct from a
   * composition whose lines are entirely Unclassified, which is what most of
   * this estate looks like and is a different statement.
   */
  costComposition: CostComposition.nullable(),
  /**
   * What agreements commit Aurora to paying for this application each year.
   *
   * Null when the caller may not see finance, exactly like `costComposition`.
   * Not null-when-empty: an application covered by no agreement returns a
   * commitment with an empty `contracts` array and a null `annualCommitment`,
   * because "no agreement covers this" and "you may not see the money" are
   * different statements and the screen says each of them differently.
   */
  contractedCommitment: ContractedCommitment.nullable(),
  /**
   * The services this application exposes, and the interfaces in front of them.
   *
   * Empty for most of the estate and that is the honest answer: 76 of Aurora's
   * 1,924 applications expose a named service. An empty array here means
   * nothing is recorded, which the drawer states rather than leaving a blank
   * panel.
   */
  services: z.array(ExposedService),
  /** The data assets this application stores or processes. Empty where none is recorded. */
  dataAssets: z.array(StoredDataAsset),
  lifecycleSince: IsoDate.nullable(),
  nextReview: IsoDate.nullable(),
  businessValue: z.array(z.object({ dimension: z.string(), rating: z.string() })),
  duplicate: z
    .object({
      overlapScore: z.number().min(0).max(1),
      duplicateOf: z.array(EntityRef),
    })
    .nullable(),
  /**
   * The rationalisation decision on record, or null when nobody has made one.
   *
   * This used to be called `recommendedAction` and the drawer rendered it under
   * "a recommendation, not a decision" — but both the route and the fixture
   * filled it from `core.application.disposition_*`, which is where a person's
   * decision is written. Calling a decision a recommendation invites a reader
   * to disregard it, and hid the fact that nothing in the interface could make
   * one. It is named for what it is, and it carries who decided and when so the
   * next person to open the drawer can see whose judgement they are looking at.
   *
   * `decidedAt` and `decidedBy` are nullable because dispositions loaded from a
   * source system arrive without either; an unattributed decision is stated as
   * unattributed rather than credited to whoever imported it.
   */
  recordedDisposition: z
    .object({
      disposition: Disposition,
      rationale: z.string(),
      confidence: z.number().min(0).max(1),
      decidedAt: Timestamp.nullable(),
      decidedBy: z.string().min(1).nullable(),
    })
    .nullable(),
  relatedProducts: z.array(
    z.object({
      product: EntityRef,
      role: RelationshipRole,
      allocationPercent: z.number().nullable(),
    }),
  ),
  relatedProcesses: z.array(z.object({ process: EntityRef, role: RelationshipRole })),
});
export type ApplicationDetail = z.infer<typeof ApplicationDetail>;

export const ApplicationPortfolioView = z.object({
  kpis: z.array(MetricValue),
  page: z.custom<Paged<ApplicationRow>>(),
  facets: z.array(
    z.object({
      field: z.string(),
      label: z.string(),
      options: z.array(z.object({ value: z.string(), label: z.string(), count: z.number() })),
    }),
  ),
  selectedApplicationId: Uuid.nullable(),
  detail: ApplicationDetail.nullable(),
});
export type ApplicationPortfolioView = z.infer<typeof ApplicationPortfolioView>;

// ─────────────────────────────────────────────────────────────────────────────
// 7. IntegrationOperationsView — /admin/integrations   (ref: 07_…png)
// ─────────────────────────────────────────────────────────────────────────────

export const ConnectorRow = z.object({
  id: Uuid,
  integrationId: z.string(),
  name: z.string(),
  category: z.string(),
  mode: z.enum([
    'FileFeed',
    'ManagedFileDrop',
    'DirectConnector',
    'ContentConnector',
    'Identity',
    'Notification',
  ]),
  connectionHealth: z.enum(['Healthy', 'Degraded', 'Down', 'Paused']),
  lastSyncAt: Timestamp.nullable(),
  objectsIngested: z.number().int().nonnegative(),
  recordsIngested: z.number().int().nonnegative(),
  errorQueueCount: z.number().int().nonnegative(),
  dataQualityPercent: z.number().min(0).max(1).nullable(),
  /** Status only. Secret values are never returned by any endpoint. */
  authenticationStatus: z.enum(['Configured', 'Expiring', 'Failed', 'NotConfigured']),
  authenticationMethod: z.string().nullable(),
  nextRefreshAt: Timestamp.nullable(),
  owner: z.string().nullable(),
});
export type ConnectorRow = z.infer<typeof ConnectorRow>;

export const IngestionRunRow = z.object({
  id: Uuid,
  runCode: z.string(),
  /**
   * Null for a run that came from a file rather than a connection.
   *
   * `ingest.ingestion_run` has always allowed either origin — a file feed has
   * an artifact and no connector, a direct connector has a connector and no
   * file — and this used to require a connector id, so the only runs the
   * administration screen could describe were the ones nobody had run yet.
   * Every uploaded workbook was a run this type could not express.
   */
  connectorId: Uuid.nullable(),
  fileName: z.string().nullable(),
  actor: OwnerRef.nullable(),
  startedAt: Timestamp,
  durationMs: z.number().int().nonnegative().nullable(),
  status: z.enum(['Queued', 'Running', 'Completed', 'Warning', 'Failed', 'Cancelled']),
  counts: z.object({
    submitted: z.number().int().nonnegative(),
    read: z.number().int().nonnegative(),
    valid: z.number().int().nonnegative(),
    warning: z.number().int().nonnegative(),
    rejected: z.number().int().nonnegative(),
    unchanged: z.number().int().nonnegative(),
    created: z.number().int().nonnegative(),
    updated: z.number().int().nonnegative(),
  }),
});
export type IngestionRunRow = z.infer<typeof IngestionRunRow>;

export const IntegrationOperationsView = z.object({
  kpis: z.array(MetricValue),
  connectors: z.array(ConnectorRow),
  healthByCategory: ChartPayload,
  healthTrend: ChartPayload,
  ingestionOverview: z.object({
    recordsIngested: z.number().int().nonnegative(),
    objectsIngested: z.number().int().nonnegative(),
    errorQueue: z.number().int().nonnegative(),
    recordsOverTime: ChartPayload,
  }),
  recentRuns: z.array(IngestionRunRow),
  topErrorTypes: z.array(z.object({ errorType: z.string(), count: z.number().int() })),
  dataQualityTrend: ChartPayload,
  dataQualitySummary: z.array(
    z.object({ dimension: z.string(), percent: z.number().min(0).max(1) }),
  ),
});
export type IntegrationOperationsView = z.infer<typeof IntegrationOperationsView>;

// ─────────────────────────────────────────────────────────────────────────────
// 8. LineageWorkbenchView — /admin/lineage/:datasetId?   (ref: 08_…png)
// ─────────────────────────────────────────────────────────────────────────────

export const LineageColumnNode = z.object({
  id: Uuid,
  column: z.enum(['source', 'staging', 'canonical']),
  label: z.string(),
  sublabel: z.string().nullable(),
  recordCount: z.number().int().nonnegative().nullable(),
  status: z.enum(['Active', 'Draft', 'Superseded', 'Error']),
  iconToken: z.string(),
});
export type LineageColumnNode = z.infer<typeof LineageColumnNode>;

export const MappingRuleRow = z.object({
  id: Uuid,
  ordinal: z.number().int().positive(),
  ruleName: z.string(),
  transformationType: z.string(),
  expression: z.string(),
  isEditable: z.boolean(),
});
export type MappingRuleRow = z.infer<typeof MappingRuleRow>;

export const LineageWorkbenchView = z.object({
  kpis: z.array(MetricValue),
  nodes: z.array(LineageColumnNode),
  edges: z.array(
    z.object({
      id: Uuid,
      fromNodeId: Uuid,
      toNodeId: Uuid,
      mappingRuleId: Uuid.nullable(),
      status: z.enum(['Active', 'Draft', 'Error']),
    }),
  ),
  selectedMapping: z
    .object({
      id: Uuid,
      name: z.string(),
      sourceLabel: z.string(),
      targetLabel: z.string(),
      mappingType: z.string(),
      status: z.string(),
      owner: z.string(),
      createdOn: IsoDate,
      lastModifiedAt: Timestamp,
      approvedBy: z.string().nullable(),
      approvedAt: Timestamp.nullable(),
      version: z.string(),
    })
    .nullable(),
  rules: z.array(MappingRuleRow),
  transformationPreview: z.object({
    sourceColumns: z.array(z.string()),
    targetColumns: z.array(z.string()),
    rows: z.array(z.record(z.string().nullable())),
    /** Preview never persists. The flag is surfaced in the UI as a banner. */
    isNonMutating: z.literal(true),
  }),
  validation: z.object({
    errors: z.number().int().nonnegative(),
    warnings: z.number().int().nonnegative(),
    info: z.number().int().nonnegative(),
    issues: z.array(
      z.object({
        severity: IssueSeverity,
        rule: z.string(),
        issue: z.string(),
        count: z.number().int().nonnegative(),
        lastOccurredAt: Timestamp,
      }),
    ),
  }),
  approvalWorkflow: z.array(
    z.object({
      stage: z.string(),
      actor: z.string().nullable(),
      status: z.enum(['Complete', 'Pending', 'Blocked']),
      occurredAt: Timestamp.nullable(),
    }),
  ),
  stewardAssignments: z.array(z.object({ role: z.string(), steward: OwnerRef.nullable() })),
});
export type LineageWorkbenchView = z.infer<typeof LineageWorkbenchView>;

// ─────────────────────────────────────────────────────────────────────────────
// 9. ScenarioComparisonView — /scenarios/:scenarioId/compare   (ref: 09_…png)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * `ApplicationConsolidation` joins the three target-operating-model types
 * because it is the only scenario this estate can ground: 91 applications carry
 * a Consolidate disposition somebody recorded, and a model over invented
 * dispositions is a calculator with a portfolio-shaped skin.
 */
export const ScenarioType = z.enum([
  'Current',
  'TargetProductModel',
  'AiEnabledTarget',
  'ApplicationConsolidation',
]);
export type ScenarioType = z.infer<typeof ScenarioType>;

export const ScenarioAssumptionControl = z.object({
  assumptionCode: z.string(),
  label: z.string(),
  value: z.number(),
  unit: z.string(),
  min: z.number().nullable(),
  max: z.number().nullable(),
  source: z.string().nullable(),
  confidence: ConfidenceBand.nullable(),
  rationale: z.string().nullable(),
});
export type ScenarioAssumptionControl = z.infer<typeof ScenarioAssumptionControl>;

/**
 * A single cell in the comparison grid. `formula` and `inputs` are carried on
 * every cell so that clicking any number reveals how it was produced — a hard
 * requirement, and the reason scenario outputs are trustworthy at all.
 */
export const ScenarioMetricCell = z.object({
  metricId: z.string(),
  label: z.string(),
  value: z.union([z.number(), z.string(), Money]).nullable(),
  unit: z.string(),
  deltaVsBaseline: z.number().nullable(),
  deltaLabel: z.string().nullable(),
  formula: z.string(),
  formulaVersion: z.string(),
  inputs: z.array(z.object({ code: z.string(), label: z.string(), value: z.string() })),
  confidence: ConfidenceBand.nullable(),
  trend: Trend.nullable(),
});
export type ScenarioMetricCell = z.infer<typeof ScenarioMetricCell>;

export const ScenarioSection = z.object({
  sectionId: z.string(),
  title: z.string(),
  cells: z.array(ScenarioMetricCell),
});
export type ScenarioSection = z.infer<typeof ScenarioSection>;

export const ScenarioColumn = z.object({
  scenarioId: Uuid,
  type: ScenarioType,
  title: z.string(),
  subtitle: z.string(),
  colorToken: z.string(),
  version: z.number().int().positive(),
  status: DecisionStatus,
  /** True when assumptions changed since the last calculation. */
  isDirty: z.boolean(),
  isReadOnly: z.boolean(),
  orgLayers: z.number().int().nonnegative().nullable(),
  averageSpan: z.number().nullable(),
  managementRatio: z.number().nullable(),
  sections: z.array(ScenarioSection),
  implementationMoves: z.array(z.string()),
});
export type ScenarioColumn = z.infer<typeof ScenarioColumn>;

export const ScenarioComparisonView = z.object({
  subject: EntityRef,
  timeHorizonLabel: z.string(),
  currency: z.string(),
  assumptions: z.array(ScenarioAssumptionControl),
  columns: z.array(ScenarioColumn),
  scenarioHealth: z.object({
    financial: ConfidenceBand,
    delivery: ConfidenceBand,
    risk: ConfidenceBand,
    feasibility: ConfidenceBand,
    overallConfidencePercent: z.number().min(0).max(100),
  }),
  impactSummary: z.array(ScenarioMetricCell),
  differences: z.array(z.object({ text: z.string(), isPositive: z.boolean() })),
  recommendation: z
    .object({
      scenarioId: Uuid,
      title: z.string(),
      rationale: z.string(),
      confidence: ConfidenceBand,
    })
    .nullable(),
  lastRecalculatedAt: Timestamp.nullable(),
  /** Blocks comparison and names what is missing. */
  missingBaselineReasons: z.array(z.string()),
});
export type ScenarioComparisonView = z.infer<typeof ScenarioComparisonView>;

// ─────────────────────────────────────────────────────────────────────────────
// 10. GovernanceCommandView — /admin/command-center   (ref: 10_…png)
// ─────────────────────────────────────────────────────────────────────────────

export const GovernanceQueue = z.object({
  queueId: z.string(),
  label: z.string(),
  iconToken: z.string(),
  pendingCount: z.number().int().nonnegative(),
  route: z.string(),
});
export type GovernanceQueue = z.infer<typeof GovernanceQueue>;

export const SlaRow = z.object({
  slaId: z.string(),
  name: z.string(),
  target: z.string(),
  achieved: z.string(),
  status: z.enum(['OnTrack', 'AtRisk', 'Breached']),
});
export type SlaRow = z.infer<typeof SlaRow>;

export const AuditRow = z.object({
  id: Uuid,
  occurredAt: Timestamp,
  actor: z.string(),
  action: z.string(),
  entityLabel: z.string(),
  detail: z.string(),
  correlationId: z.string(),
});
export type AuditRow = z.infer<typeof AuditRow>;

export const GovernanceCommandView = z.object({
  kpis: z.array(MetricValue),
  slas: z.array(SlaRow),
  queues: z.array(GovernanceQueue),
  totalPendingItems: z.number().int().nonnegative(),
  dataQualityScorecard: z.object({
    /**
     * Null where no data quality rule has been configured at all.
     *
     * Zero would report an estate that had been profiled and failed every
     * check, which is the opposite of the truth — nothing has been profiled,
     * because nobody has written a rule to profile it against.
     */
    overallPercent: z.number().min(0).max(100).nullable(),
    buckets: z.array(
      z.object({ label: z.string(), percent: z.number().min(0).max(1), colorToken: z.string() }),
    ),
    totalRules: z.number().int().nonnegative(),
    passed: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
    waived: z.number().int().nonnegative(),
  }),
  roleSummary: z.object({
    totalUsers: z.number().int().nonnegative(),
    byRole: z.array(z.object({ role: z.string(), count: z.number().int().nonnegative() })),
  }),
  taxonomySummary: z.array(
    z.object({ entityType: z.string(), count: z.number().int(), changeCount: z.number().int() }),
  ),
  stewardshipTasks: z.array(
    z.object({ label: z.string(), count: z.number().int(), isOverdue: z.boolean() }),
  ),
  approvalBreakdown: z.array(
    z.object({ status: z.string(), count: z.number().int(), colorToken: z.string() }),
  ),
  auditTrail: z.array(AuditRow),
  dataQualityExceptions: z.array(
    z.object({
      severity: IssueSeverity,
      ruleCode: z.string(),
      entityLabel: z.string(),
      issue: z.string(),
      ageLabel: z.string(),
    }),
  ),
  integrationAlerts: z.array(
    z.object({
      system: z.string(),
      status: z.enum(['Healthy', 'Warning', 'Error']),
      alert: z.string().nullable(),
      sinceLabel: z.string().nullable(),
    }),
  ),
  configuration: z.array(
    z.object({ label: z.string(), iconToken: z.string(), summary: z.string(), route: z.string() }),
  ),
  notificationRules: z.array(
    z.object({ label: z.string(), iconToken: z.string(), activeCount: z.number().int() }),
  ),
  release: z.object({
    nextWindow: IsoDate.nullable(),
    nextWindowLabel: z.string().nullable(),
    inProgress: z.string().nullable(),
    changeRequests: z.number().int().nonnegative(),
    health: z.enum(['Healthy', 'Degraded', 'Blocked']),
  }),
});
export type GovernanceCommandView = z.infer<typeof GovernanceCommandView>;

// ── Allocation guard ─────────────────────────────────────────────────────────

export { AllocationStatus };
